import React from "react";
import { Controller, useFormContext } from "react-hook-form";
import { intl } from "LocaleWrapper";

import { Box, Grid2 as Grid, Typography } from "@mui/material";
import { FormGroup, Button } from "@cofano/cck";

import { useConfirm } from "components/hooks/useConfirm";

import { Language } from "imports/Utils";

import { ShipSchemaInput, ShipSchemaOutput } from "view/ships/utils/ship.schema";
import ShipBayplan from "view/ships/components/bayplan/ShipBayPlan";

import { BargeType } from "__generated__/enums";

const ShipHoldsForm = ({
	openSelectBayPlanTemplateDialog,
	openCreateBayPlanTemplateDialog,
	openShipHoldDialog,
}: {
	openSelectBayPlanTemplateDialog: () => void;
	openCreateBayPlanTemplateDialog: () => void;
	openShipHoldDialog: () => void;
}) => {
	const { control, setValue, watch } = useFormContext<ShipSchemaInput, unknown, ShipSchemaOutput>();

	const confirm = useConfirm();

	const bayPlan = watch("bayPlan");
	const bargeType = watch("bargeType");
	const hasBayPlan = !!(bayPlan?.rows && bayPlan.bays && bayPlan.tiers);

	return (
		<div style={{ padding: 16 }}>
			<div>
				{hasBayPlan ? (
					<React.Fragment>
						<Grid container spacing={3}>
							<Grid size={12}>
								<div style={{ display: "flex" }}>
									<div style={{ display: "flex", flexGrow: 1 }}>
										<Button
											style={{ marginRight: 8 }}
											onClick={() => openSelectBayPlanTemplateDialog()}
											variant="outlined"
											size="small">
											{Language("USE_TEMPLATE")}
										</Button>

										<Button
											style={{ marginRight: 8 }}
											onClick={() => openCreateBayPlanTemplateDialog()}
											variant="outlined"
											size="small">
											{Language("SAVE_AS_TEMPLATE")}
										</Button>
									</div>

									<Button
										onClick={() => {
											confirm({
												type: "DELETE",
											}).then(() => {
												setValue("bayPlan", null);
											});
										}}
										color="danger"
										size="small">
										{Language("DELETE")}
									</Button>
								</div>
							</Grid>

							<Grid size={4}>
								<FormGroup label={Language("BAYPLAN_ROWS")} overflow="hidden">
									{bayPlan.rows}
								</FormGroup>
							</Grid>

							<Grid size={4}>
								<FormGroup label={Language("BAYPLAN_BAYS")} overflow="hidden">
									{bayPlan.bays}
								</FormGroup>
							</Grid>

							<Grid size={4}>
								<FormGroup label={Language("BAYPLAN_TIERS")} overflow="hidden">
									{bayPlan.tiers}
								</FormGroup>
							</Grid>
						</Grid>

						<Box mt={2}>
							<Controller
								name="bayPlan.properties"
								control={control}
								render={({ field }) => (
									<ShipBayplan
										value={field.value}
										onChange={properties => field.onChange(properties)}
										rows={bayPlan.rows}
										bays={bayPlan.bays}
										tiers={bayPlan.tiers} />
								)} />
						</Box>
					</React.Fragment>
				) : (
					<React.Fragment>
						<Button
							disabled={bargeType !== BargeType.ContainerShip}
							onClick={() => openShipHoldDialog()}
							size="small"
							variant="outlined">
							{Language("BAYPLAN_ADD")}
						</Button>

						{bargeType !== BargeType.ContainerShip && (
							<Typography variant="caption">
								{intl.formatMessage({ id: "SELECT_BAYPLAN_BARGE_TYPE_WARNING" })}
							</Typography>
						)}
					</React.Fragment>
				)}
			</div>
		</div>
	);
};

export default ShipHoldsForm;
